import { currencyFormat } from "@Helpers/support"

/**
 * Raw price data from the server
 */
export interface PriceData {
  amount: number
  currency: string
}

/**
 * Price smart object with formatting methods
 */
export interface Price extends PriceData {
  toHuman: (locale?: string) => string
  isZero: () => boolean
}

/**
 * Default price data for free/zero amounts
 */
export const ZERO_PRICE_DATA: PriceData = {
  amount: 0,
  currency: "USD",
}

/**
 * Creates a Price smart object from raw data.
 *
 * @param data The raw price data (amount in major units, e.g., 12.34 for $12.34)
 */
export default function PriceModel(data: PriceData | null): Price {
  const priceData = data ?? ZERO_PRICE_DATA

  const that: Price = {
    amount: priceData.amount,
    currency: priceData.currency,

    /**
     * Returns a human-readable formatted price string (e.g., "$12.34")
     */
    toHuman(locale?: string): string {
      return currencyFormat(priceData.amount, priceData.currency, locale)
    },

    /**
     * Returns true if the price is zero
     */
    isZero(): boolean {
      return priceData.amount === 0
    },
  }

  return that
}

/**
 * Creates a zero price object
 */
export function createZeroPrice(currency: string = "USD"): Price {
  return PriceModel({ amount: 0, currency })
}
